import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import CartProductWrapper from '../CartProductWrapper/CartProductWrapper';
import CartSelect from '../CartSelect/CartSelect';
import Loading from 'components/Common/Loading/Loading';
import Button from 'components/UI/Buttons/Button/Button';
import { cartContext } from 'contexts/cartContext';
import { apiUrls } from 'services/api-urls';
import { formatPrice } from 'services/formatter';
import { CartOsGProduct, CartSpecialOffer } from 'types/Cart';
import { CatalogProductBodyRequest } from 'types/CatalogProductBodyRequest';

import styles from './CartSpecialOffers.module.scss';

export default function CartSpecialOffers({
  specialOffers,
}: {
  specialOffers: CartSpecialOffer[];
}): JSX.Element {
  const { t } = useTranslation(['translation', 'checkout']);
  const { addToMyBasket } = useContext(cartContext);
  const [offers, setOffers] = useState<{
    [codeAm: string]: orderedGifts;
  }>();
  const [offer, setOffer] = useState<orderedGifts>();
  const [product, setProduct] = useState<CartOsGProduct>();
  const [isLoading, setIsLoading] = useState(false);
  const choices = useMemo(() => renderChoices(), [offers]);
  const sizes = useMemo(() => renderSizes(), [offer]);

  useEffect(() => {
    const skus: CatalogProductBodyRequest[] = specialOffers.map(
      specialOffer => {
        return {
          sku: specialOffer.products[0].sku,
          sizeLabel: specialOffer.products[0].sizeLabel,
          catalogSku: specialOffer.products[0].reference,
        };
      }
    );

    if (skus.length) {
      Promise.all(
        skus.map(sku =>
          fetch(
            apiUrls.getProductInfos(sku.sku, sku.catalogSku, sku.sizeLabel),
            {
              headers: {
                'X-AUTH-TOKEN': `${process.env.REACT_APP_API_SYMFONY_API_KEY}`,
              },
            }
          ).then(res => res.json())
        )
      ).then(catalogProducts => {
        const orderedOffers = specialOffers.reduce((acc, specialOffer, i) => {
          const { attributes, products } = specialOffer;
          const { startingPrice } = attributes;
          const { name } = catalogProducts[i];
          return {
            ...acc,
            [String(attributes.codeAm)]: {
              codeAm: attributes.codeAm,
              name: name,
              price: products[0].price,
              products: products,
              reference: products[0].reference,
              ...(startingPrice && { startingPrice: startingPrice }),
            },
          };
        }, {});
        setOffers(orderedOffers);
      });
    }
  }, [specialOffers]);

  useEffect(() => {
    if (offers) {
      const offer = Object.values(offers)[0];
      setOffer(offer);
      setProduct(offer.products.length === 1 ? offer.products[0] : undefined);
    }
  }, [offers]);

  function handleChangeSize(sku: string) {
    const selectedProduct = offer?.products.find(product => product.sku == sku);
    selectedProduct && setProduct(selectedProduct);
  }

  function handleConfirm() {
    if (!product) return;

    setIsLoading(true);
    addToMyBasket({
      catalogSku: product.reference,
      sku: product.sku,
      sizeLabel: product.sizeLabel,
      qty: '1',
      url: '',
      so: product.sku + '|' + offer?.codeAm,
    }).finally(() => setIsLoading(false));
  }

  function isInStock(stockStatus: string) {
    return t('AVAILABLE').toLowerCase() === stockStatus.toLowerCase();
  }

  function renderChoices(): JSX.Element {
    if (!offers) return <></>;

    const options = Object.keys(offers).map(key => {
      return { label: offers[key].name, value: key };
    });

    if (specialOffers.length === 1) {
      return <>{options[0].label}</>;
    }

    return (
      <CartSelect
        onChange={newValue => setOffer(newValue.value)}
        options={options}
      />
    );
  }

  function renderSizes(): JSX.Element {
    if (offer?.products.length !== 1) {
      const options = offer?.products.map(product => {
        const label = `${product.choice} ${
          offer.startingPrice ? `(${formatPrice(product.price)})` : ''
        }`;

        return {
          label: label,
          value: product.sku,
          isDisabled: !isInStock(product.stock),
        };
      });

      return (
        <CartSelect
          onChange={newValue => handleChangeSize(newValue.value)}
          options={options}
          placeholder={t('LABEL_CHOICES')}
        />
      );
    }

    return (
      <>
        <b>{t('LABEL_TWO_POINTS', { label: t('LABEL_CHOICES') })}</b>
        {product?.choice}
      </>
    );
  }

  if (!offer) {
    return <></>;
  }

  return (
    <>
      <CartProductWrapper
        classes={{
          container: styles.container,
          details: styles.details,
          title: styles.title,
        }}
        image={product ? product.frontView : offer.products[0].frontView}
        title={t('LABEL_YOUR_SPECIAL_OFFER')}
      >
        <p>{choices}</p>
        <p>{sizes}</p>
        <p>
          <b>{t('LABEL_TWO_POINTS', { label: t('LABEL_QUANTITY') })}</b>1
        </p>
        <p>
          <b>
            {t('LABEL_TWO_POINTS', {
              label: t('LABEL_UNIT_PRICE'),
            })}
          </b>
          {t('LABEL_FROM') + ' ' + formatPrice(offer.price)}
        </p>
        <Button
          className={styles.addButton}
          disabled={!product}
          model={'primary'}
          onClick={handleConfirm}
        >
          {t('LABEL_ADD_TO_MY_BASKET')}
        </Button>
      </CartProductWrapper>
      {isLoading && <Loading loading />}
    </>
  );
}

type orderedGifts = {
  codeAm: string;
  name: string;
  price: string;
  startingPrice?: string;
  products: sku[];
  reference: string;
};

type sku = {
  choice: string;
  codeAm: string;
  price: string;
  reference: string;
  sizeCode: string;
  sizeLabel: string;
  sku: string;
  stock: string;
  configurableView: string;
  frontView: string;
};
